import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLocale } from "@/contexts/LocaleContext";

interface NewsletterFormProps {
  variant?: "hero" | "inline" | "compact";
}

export default function NewsletterForm({ variant = "hero" }: NewsletterFormProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { t } = useLocale();

  const subscribeMutation = trpc.newsletter.subscribe.useMutation({
    onSuccess: (data) => {
      setSubmitted(true);
      if (data.alreadySubscribed) {
        toast.info(t("newsletter.alreadySubscribed"));
      } else if (data.reactivated) {
        toast.success(t("newsletter.reactivated"));
      } else {
        toast.success(t("newsletter.subscribeSuccess"));
      }
    },
    onError: (err) => {
      toast.error(err.message || t("newsletter.subscribeError"));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error(t("newsletter.emailRequired")); return; }
    subscribeMutation.mutate({ email: email.trim(), name: name.trim() || undefined });
  };

  // ─── Hero variant ─────────────────────────────────────────
  if (variant === "hero") {
    return (
      <section className="relative py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/5 via-transparent to-gold/5 rounded-2xl" />
        <div className="relative container max-w-2xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 text-gold text-sm font-medium bg-gold/10 px-4 py-1.5 rounded-full">
            <Mail className="h-4 w-4" />
            {t("newsletter.badge")}
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold">
            {t("newsletter.title")}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mx-auto">
            {t("newsletter.desc")}
          </p>

          {submitted ? (
            <div className="flex items-center justify-center gap-3 py-4 px-6 bg-green-500/10 border border-green-500/20 rounded-xl">
              <CheckCircle2 className="h-6 w-6 text-green-400 shrink-0" />
              <p className="text-green-300 font-medium">{t("newsletter.success")}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 max-w-md mx-auto">
              <Input
                type="text"
                placeholder={t("newsletter.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-input border-border h-11"
              />
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t("newsletter.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-input border-border h-11 flex-1"
                  required
                />
                <Button
                  type="submit"
                  className="bg-gold text-navy-dark hover:bg-gold-light font-semibold h-11 px-6 shrink-0"
                  disabled={subscribeMutation.isPending}
                >
                  {subscribeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("newsletter.submit")
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("newsletter.noSpam")}
              </p>
            </form>
          )}
        </div>
      </section>
    );
  }

  // ─── Inline variant ───────────────────────────────────────
  if (variant === "inline") {
    return (
      <div className="border border-border/50 rounded-xl p-6 md:p-8 bg-card/50 space-y-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mt-0.5">
            <Sparkles className="h-5 w-5 text-gold" />
          </div>
          <div>
            <h3 className="font-serif font-bold text-lg">{t("newsletter.inlineTitle")}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("newsletter.inlineDesc")}
            </p>
          </div>
        </div>

        {submitted ? (
          <div className="flex items-center gap-2 py-3 px-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
            <p className="text-sm text-green-300 font-medium">{t("newsletter.inlineSuccess")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              type="email"
              placeholder={t("newsletter.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-input border-border h-10 flex-1"
              required
            />
            <Button
              type="submit"
              className="bg-gold text-navy-dark hover:bg-gold-light font-semibold h-10 px-5 shrink-0"
              disabled={subscribeMutation.isPending}
            >
              {subscribeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Mail className="mr-1.5 h-4 w-4" /> {t("newsletter.submit")}
                </>
              )}
            </Button>
          </form>
        )}
      </div>
    );
  }

  // ─── Compact variant ──────────────────────────────────────
  return (
    <div className="space-y-3">
      <h4 className="font-serif font-bold text-base">{t("newsletter.compactTitle")}</h4>
      <p className="text-sm text-muted-foreground">
        {t("newsletter.compactDesc")}
      </p>
      {submitted ? (
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-400" />
          <span className="text-sm text-green-300">{t("newsletter.subscribed")}</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="email"
            placeholder={t("newsletter.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-input border-border h-9 text-sm flex-1"
            required
          />
          <Button
            type="submit"
            size="sm"
            className="bg-gold text-navy-dark hover:bg-gold-light font-semibold h-9 px-4 shrink-0"
            disabled={subscribeMutation.isPending}
            aria-label={t("newsletter.subscribe") || "Suscribirse"}
          >
            {subscribeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Mail className="h-3.5 w-3.5" aria-hidden />}
          </Button>
        </form>
      )}
    </div>
  );
}
